import React, { useEffect, useState, useCallback } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Table, 
    Typography, 
    Spin, 
    Alert, 
    Button, 
    Space, 
    Tag
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    UserOutlined, 
    DollarOutlined, 
    TransactionOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    ExpandOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import { 
    AreaChart, 
    Area, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';
import { useAdmin } from '../../hooks/useAdmin';
import '../../styles/AdminDashboard.css';

const { Title, Text } = Typography;

const AdminDashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getDashboard, getHistoricalStats, loading, error } = useAdmin();
    const [dashboardData, setDashboardData] = useState(null);

    const loadDashboard = useCallback(async () => {
        const [dashRes, growthRes] = await Promise.all([
            getDashboard(),
            getHistoricalStats()
        ]);
        
        if (dashRes.success && dashRes.data) {
            setDashboardData(dashRes.data);
        }
        if (growthRes && growthRes.success) {
            setGrowthStats(growthRes.data);
        }
    }, [getDashboard, getHistoricalStats]);

    const [growthStats, setGrowthStats] = useState(null);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    if (loading && !dashboardData) {
        return (
            <div className="admin-loading-container">
                <Spin size="large" tip={t('admin.dashboard_view.init_message')} />
            </div>
        );
    }

    if (error) {
        return <Alert message="System Error" description={error} type="error" showIcon />;
    }

    const stats = dashboardData?.stats || {};
    
    // Mock data for charts
    const volumeData = growthStats?.volume?.map(v => ({
        name: new Date(v.date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' }),
        volume: parseFloat(v.volume)
    })) || [
        { name: 'Mon', volume: 0 },
        { name: 'Tue', volume: 0 },
        { name: 'Wed', volume: 0 },
        { name: 'Thu', volume: 0 },
        { name: 'Fri', volume: 0 },
        { name: 'Sat', volume: 0 },
        { name: 'Sun', volume: 0 },
    ];

    const userDataChart = growthStats?.users?.map(u => ({
        name: new Date(u.date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' }),
        users: parseInt(u.count)
    })) || [
        { name: 'Mon', users: 0 },
        { name: 'Tue', users: 0 },
        { name: 'Wed', users: 0 },
        { name: 'Thu', users: 0 },
        { name: 'Fri', users: 0 },
        { name: 'Sat', users: 0 },
        { name: 'Sun', users: 0 },
    ];

    const userColumns = [
        {
            title: t('admin.dashboard_view.tables.user_col'),
            key: 'user',
            render: (record) => (
                <Space>
                    <UserOutlined />
                    <Text strong>{record.username}</Text>
                </Space>
            )
        },
        {
            title: t('admin.dashboard_view.tables.email_col'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: t('admin.dashboard_view.tables.status_col'),
            dataIndex: 'account_status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: t('admin.dashboard_view.tables.registered_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : (i18n.language === 'es' ? 'es-ES' : 'en-US')),
        },
    ];

    return (
        <div className="admin-dashboard-inner animate-fade-in">
      <div className="glass-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} className="admin-page-title">{t('admin.dashboard_view.title')}</Title>
            <Text className="admin-page-subtitle">{t('admin.dashboard_view.subtitle')}</Text>
          </div>
          <Button 
            type="primary" 
            icon={<HistoryOutlined />} 
            onClick={() => navigate('/admin/audit-logs')}
            className="glass-button"
          >
            {t('admin.dashboard_view.audit_logs')}
          </Button>
        </div>
      </div>

      {/* Top Stats Row */}
      <Row gutter={[20, 20]} className="stats-row" style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                <UserOutlined className="text-2xl text-blue-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{t('admin.dashboard_view.stats.users')}</div>
                <div className="text-2xl font-black text-white">{stats.total_users || 0}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20">
                <DollarOutlined className="text-2xl text-green-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{t('admin.dashboard_view.stats.investments')}</div>
                <div className="text-2xl font-black text-white">${parseFloat(stats.active_investments || 0).toLocaleString()}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-purple-500/10 border border-purple-500/20">
                <TransactionOutlined className="text-2xl text-purple-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{t('admin.dashboard_view.stats.volume_24h')}</div>
                <div className="text-2xl font-black text-white">${parseFloat(stats.today_volume || 0).toLocaleString()}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-orange-500/10 border border-orange-500/20">
                <ClockCircleOutlined className="text-2xl text-orange-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{t('admin.dashboard_view.stats.pending_withdrawals')}</div>
                <div className="text-2xl font-black text-white">{stats.pending_withdrawals_count || 0} {t('admin.dashboard_view.stats.requests')}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card className="glass-card chart-container-premium" title={<span className="text-white">{t('admin.dashboard_view.charts.trading_perf')}</span>} extra={<ExpandOutlined className="text-slate-500" />}>
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={volumeData}>
                  <defs>
                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v/1000}k`} />
                  <RechartsTooltip 
                    contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                    itemStyle={{ color: '#818cf8' }}
                  />
                  <Area type="monotone" dataKey="volume" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorVolume)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="glass-card chart-container-premium" title={<span className="text-white">{t('admin.dashboard_view.charts.registrations')}</span>} extra={<InfoCircleOutlined className="text-slate-500" />}>
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={userDataChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <RechartsTooltip 
                    contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                  />
                  <Bar dataKey="users" fill="#6366f1" radius={[6, 6, 0, 0]}>
                    {userDataChart.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === userDataChart.length - 1 ? '#4ade80' : 'rgba(99, 102, 241, 0.4)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>


            {/* Recent Activity Table */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <Card 
                        title={t('admin.dashboard_view.tables.recent_users')} 
                        extra={<Button type="link" onClick={() => navigate('/admin/users')}>{t('admin.dashboard_view.tables.manage_all')}</Button>}
                    >
                        <Table
                            dataSource={dashboardData?.recent_users || []}
                            columns={userColumns}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card 
                        title={t('admin.dashboard_view.tables.priority_withdrawals')} 
                        extra={<Button type="link" onClick={() => navigate('/admin/transactions')}>{t('admin.dashboard_view.tables.view_all')}</Button>}
                    >
                        <Table
                            dataSource={dashboardData?.pending_withdrawals || []}
                            columns={[
                                {
                                    title: t('admin.dashboard_view.tables.user_col'),
                                    dataIndex: 'username',
                                    key: 'username',
                                    render: (text) => <Text strong>{text}</Text>
                                },
                                {
                                    title: t('admin.dashboard_view.tables.amount_col'),
                                    key: 'amount',
                                    render: (_, record) => <Text type="danger">{parseFloat(record.amount).toFixed(2)} {record.symbol}</Text>
                                },
                                {
                                    title: t('admin.dashboard_view.tables.date_col'),
                                    dataIndex: 'created_at',
                                    key: 'created_at',
                                    render: (date) => new Date(date).toLocaleTimeString(i18n.language === 'fr' ? 'fr-FR' : (i18n.language === 'es' ? 'es-ES' : 'en-US')),
                                },
                                {
                                    title: t('admin.dashboard_view.tables.action_col'),
                                    key: 'action',
                                    render: () => <Button size="small" type="primary">{t('admin.dashboard_view.tables.validate')}</Button>
                                }
                            ]}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AdminDashboard;